import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { addPurchase } from "../lib/purchases";

const fmt = (a, c) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: c || "EUR", maximumFractionDigits: 0 }).format(a || 0);

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", attempts: 0, data: null, error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Aucune session fournie" });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          addPurchase(sessionId);
          setState({ status: "ok", data });
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({ status: "fail", data });
          return;
        }
        if (attempts >= 8) {
          setState({ status: "timeout", data });
          return;
        }
        setState((s) => ({ ...s, status: "polling", attempts, data }));
        setTimeout(tick, 2000);
      } catch (e) {
        if (!cancelled) setState({ status: "error", error: e?.response?.data?.detail || "Erreur" });
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-6 lg:px-10 py-24 text-center" data-testid="page-checkout-success">
      {state.status === "polling" && (
        <>
          <Loader2 size={36} className="mx-auto animate-spin text-[var(--gai-red)]" />
          <h1 className="serif text-3xl mt-6">Vérification du paiement…</h1>
          <p className="mt-3 text-[var(--gai-grey-600)]">Tentative {state.attempts} / 8</p>
        </>
      )}
      {state.status === "ok" && (
        <>
          <CheckCircle2 size={48} className="mx-auto text-[var(--gai-red)]" />
          <div className="overline mt-6 mb-3 text-[var(--gai-red)]">Paiement confirmé</div>
          <h1 className="serif text-4xl md:text-5xl leading-tight">Bienvenue.</h1>
          <p className="mt-5 text-lg text-[var(--gai-grey-700)]">
            Votre {state.data.kind === "magazine" ? "magazine" : "article"} est désormais accessible — montant débité : {fmt(state.data.amount, state.data.currency)}.
          </p>
          <div className="mt-10 flex gap-3 justify-center">
            <Link to="/articles" className="gai-btn-gold" data-testid="success-back-articles">Reprendre la lecture</Link>
            <Link to="/magazine" className="gai-btn-outline">Le kiosque</Link>
          </div>
          <p className="mt-8 text-xs text-[var(--gai-grey-500)]">
            Un reçu vous a été envoyé par email. Votre accès est lié à ce navigateur ; pensez à créer un compte pour le retrouver partout.
          </p>
        </>
      )}
      {state.status === "fail" && (
        <>
          <AlertCircle size={36} className="mx-auto text-red-400" />
          <h1 className="serif text-3xl mt-6">Paiement non finalisé</h1>
          <p className="mt-3 text-[var(--gai-grey-600)]">Vous pouvez réessayer ou nous contacter.</p>
          <button onClick={() => navigate(-1)} className="mt-6 gai-btn-outline">Retour</button>
        </>
      )}
      {state.status === "timeout" && (
        <>
          <Loader2 size={36} className="mx-auto text-[var(--gai-red)]" />
          <h1 className="serif text-3xl mt-6">Vérification trop longue</h1>
          <p className="mt-3 text-[var(--gai-grey-600)]">Le paiement est en cours de traitement. Réessayez dans un instant.</p>
          <button onClick={() => window.location.reload()} className="mt-6 gai-btn-gold">Réessayer</button>
        </>
      )}
      {state.status === "error" && (
        <>
          <AlertCircle size={36} className="mx-auto text-red-400" />
          <h1 className="serif text-3xl mt-6">Erreur</h1>
          <p className="mt-3 text-[var(--gai-grey-600)]">{state.error}</p>
        </>
      )}
    </div>
  );
}
